<script setup lang="ts">
import RecursiveOrganizationMenu from '@/components/organizations/RecursiveOrganizationMenu.vue';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeStore } from '@/stores/employee';
import type { AccessibleOrganization, Organization } from '@/types/organization';
import toast from '@/utils/toast';
import { computed, ref, watch } from 'vue';

const authStore = useAuthStore();
const employeeStore = useEmployeeStore();

const menuOpenId = ref<string>(authStore.currentOrganization?.parent ?? '');

const label = computed(() => {
    if (authStore.currentOrganization == null) return 'Selecteer een bedrijf';
    return authStore.currentOrganization.type;
});

async function handleContinue(): Promise<void> {
    if (!authStore.currentOrganization) return;

    const setCurrentOrganizationResult = await employeeStore.setCurrentOrganization(authStore.currentOrganization._id);

    if (setCurrentOrganizationResult.status != 200) {
        toast.error('FailedToSetCurrentOrganization');
        return;
    }

    await router.push({ name: 'dashboard', replace: true });

    toast.success('SwitchedOrganization');
}

async function selectOrg(organization: Organization) {
    if (!organization) return;

    authStore.currentOrganization = organization as AccessibleOrganization;
    handleContinue();
}

function toggleChildMenu(id: string) {
    if (menuOpenId.value !== id) {
        menuOpenId.value = id;
    } else {
        menuOpenId.value = '';
    }
}

watch(
    () => authStore.currentOrganization,
    (newVal, oldVal) => {
        if (!newVal) {
            authStore.currentOrganization = oldVal;
        }
    }
);
</script>

<template>
    <div v-if="authStore.accessibleOrganizations" id="pickOrganizationMenu">
        <v-autocomplete
            v-model="authStore.currentOrganization"
            :key="authStore.currentOrganization?._id"
            return-object
            item-title="name"
            item-value="_id"
            :items="authStore.accessibleOrganizations"
            :label="label"
            class="bg-main-dark text-white"
            hide-details
            :hide-selected="!authStore.isSuperAdmin()"
            @update:model-value="handleContinue"
            :menu-props="{ closeOnContentClick: true, closeOnBack: true }"
            transition="fade"
            :clearable="false"
            id="organization-autocomplete"
        >
            <template v-slot:item="{ item, props }">
                <v-list-item v-bind="props" @click="selectOrg(item)">
                    <template #title>
                        <span :class="{ 'font-weight-bold text-main': authStore.currentOrganization?._id === item._id }">
                            {{ item.name }}
                            <v-icon
                                v-if="item.children && item.children.length > 0"
                                :icon="menuOpenId === item._id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                                class="hover-main text-main font-weight-bold"
                                @click.stop="toggleChildMenu(item._id)"
                            />
                        </span>
                    </template>

                    <span
                        style="font-size: 12px"
                        class="font-weight-bold"
                        :class="item.parent ? 'text-alt' : 'text-main'"
                        v-if="!authStore.isSuperAdmin()"
                    >
                        {{ item.type }}
                    </span>

                    <RecursiveOrganizationMenu v-if="authStore.isSuperAdmin()" :organization="item" :menuOpenId="menuOpenId" />
                </v-list-item>
            </template>
        </v-autocomplete>
    </div>
</template>

<style scoped></style>
